import { createFileRoute, Link } from "@tanstack/react-router";
import { Shield, Lock, Database, Cookie, Mail, ArrowRight } from "lucide-react";

export const Route = createFileRoute("/privacidade")({
  head: () => ({
    meta: [
      { title: "Política de Privacidade — MEI Toolbox" },
      {
        name: "description",
        content:
          "Política de privacidade do MEI Toolbox: sem cadastro obrigatório e sem coleta de dados pessoais — seus PDFs e conversas ficam salvos no seu próprio dispositivo.",
      },
      { property: "og:title", content: "Política de Privacidade — MEI Toolbox" },
      {
        property: "og:description",
        content:
          "Transparência total: o MEI Toolbox não coleta dados pessoais. Entenda nossa política.",
      },
      { property: "og:url", content: "https://meitoolbox.com.br/privacidade" },
    ],
    links: [{ rel: "canonical", href: "https://meitoolbox.com.br/privacidade" }],
  }),
  component: PrivacidadePage,
});

const ultimaAtualizacao = "4 de maio de 2026";

function PrivacidadePage() {
  return (
    <>
      {/* HERO */}
      <section className="relative overflow-hidden border-b border-border">
        <div className="absolute inset-0 bg-gradient-hero opacity-95" />
        <div className="relative container mx-auto px-4 py-16 md:py-20 text-white">
          <div className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 backdrop-blur px-4 py-1.5 text-xs font-semibold uppercase tracking-wider">
            <Shield className="h-3.5 w-3.5" />
            Privacidade em primeiro lugar
          </div>
          <h1 className="mt-5 font-display text-4xl md:text-5xl font-extrabold leading-tight max-w-3xl text-balance">
            Política de Privacidade
          </h1>
          <p className="mt-4 text-lg text-white/85 max-w-2xl">
            Resumo honesto: não pedimos cadastro, não coletamos seus dados pessoais e nada do que
            você digita sai do seu dispositivo.
          </p>
          <p className="mt-3 text-sm text-white/70">Última atualização: {ultimaAtualizacao}</p>
        </div>
      </section>

      {/* DESTAQUES */}
      <section className="container mx-auto px-4 py-12">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Highlight
            icon={<Lock className="h-5 w-5" />}
            title="Sem login"
            text="Você usa tudo direto, sem criar conta nem informar e-mail."
          />
          <Highlight
            icon={<Database className="h-5 w-5" />}
            title="Dados locais"
            text="Tudo o que você digita fica no localStorage do seu navegador."
          />
          <Highlight
            icon={<Cookie className="h-5 w-5" />}
            title="Cookies e Anúncios"
            text="Usamos cookies para anúncios personalizados e estatísticas."
          />
          <Highlight
            icon={<Shield className="h-5 w-5" />}
            title="LGPD"
            text="Em conformidade com a Lei Geral de Proteção de Dados."
          />
        </div>
      </section>

      {/* CONTEÚDO */}
      <section className="container mx-auto px-4 pb-20 max-w-3xl">
        <article className="prose prose-neutral max-w-none space-y-8 text-muted-foreground leading-relaxed">
          <Block title="1. Quem somos">
            <p>
              O <strong className="text-foreground">MEI Toolbox</strong> (CNPJ{" "}
              <strong className="text-foreground">63.046.089/0001-47</strong>) é uma plataforma de
              ferramentas online voltada para microempreendedores individuais (MEI) brasileiros, com
              versão gratuita e versão Pro paga. Atuamos como{" "}
              <strong className="text-foreground">controlador dos dados</strong> tratados nesta
              plataforma, nos termos da LGPD.
            </p>
          </Block>

          <Block title="2. Quais dados coletamos">
            <p>
              <strong className="text-foreground">Ferramentas gratuitas:</strong> não exigem
              cadastro. Tudo o que você digita (faturamento, custos, dados de clientes, recibos
              etc.) é processado{" "}
              <strong className="text-foreground">localmente no seu navegador</strong> e armazenado
              no <code className="bg-surface px-1.5 py-0.5 rounded text-xs">localStorage</code> do
              seu dispositivo. Essas informações{" "}
              <strong className="text-foreground">nunca chegam aos nossos servidores</strong>.
            </p>
            <p>
              <strong className="text-foreground">Conta e plano Pro:</strong> ao criar uma conta ou
              assinar o Pro, coletamos:
            </p>
            <ul className="list-disc pl-5 space-y-1">
              <li>
                <strong className="text-foreground">E-mail e nome</strong> — para autenticação,
                identificação e comunicação sobre a conta.{" "}
                <em>Base legal: execução do contrato.</em>
              </li>
              <li>
                <strong className="text-foreground">Dados de assinatura</strong> (status, plano,
                datas) — para liberar/restringir acesso aos recursos Pro.{" "}
                <em>Base legal: execução do contrato.</em>
              </li>
              <li>
                <strong className="text-foreground">Mensagens enviadas ao assistente de IA</strong>{" "}
                — processadas por nossos provedores de IA para gerar respostas.{" "}
                <em>Base legal: execução do contrato.</em>
              </li>
              <li>
                <strong className="text-foreground">Comentários e avaliações</strong> que você
                publica — exibidos publicamente conforme sua escolha.{" "}
                <em>Base legal: consentimento.</em>
              </li>
              <li>
                <strong className="text-foreground">Dados de pagamento</strong> (cartão, endereço de
                cobrança) — coletados e processados{" "}
                <strong className="text-foreground">diretamente pela Paddle.com</strong>, nosso
                Merchant of Record. Não armazenamos dados de cartão em nossos servidores.
              </li>
            </ul>
          </Block>

          <Block title="3. Dados técnicos anônimos">
            <p>
              Como qualquer site da internet, podemos coletar dados técnicos básicos e anônimos para
              entender o uso geral da plataforma, como:
            </p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Páginas visitadas (sem associação a uma pessoa)</li>
              <li>Tipo de dispositivo e navegador</li>
              <li>Origem da visita (ex: Google, link direto)</li>
              <li>Tempo aproximado de permanência</li>
            </ul>
            <p>
              Esses dados são <strong className="text-foreground">agregados e anônimos</strong>,
              usados apenas para melhorar o produto. Não fazemos perfilamento publicitário.
            </p>
          </Block>

          <Block title="4. Cookies e Anúncios (Google AdSense)">
            <p>
              Além dos cookies técnicos essenciais (como preferências de tema), exibimos anúncios
              servidos pelo <strong className="text-foreground">Google AdSense</strong>. O Google
              utiliza cookies (como o cookie DART) para veicular anúncios com base nas visitas
              anteriores dos usuários a este e a outros sites na Internet.
            </p>
            <p>
              Os usuários podem desativar a publicidade personalizada acessando as{" "}
              <a
                href="https://settings.google.com/ads/preferences"
                target="_blank"
                rel="noopener noreferrer"
                className="underline hover:text-foreground"
              >
                Configurações de Anúncios do Google
              </a>
              . Alternativamente, você pode desativar o uso de cookies de terceiros para publicidade
              personalizada visitando o site{" "}
              <a
                href="https://optout.aboutads.info"
                target="_blank"
                rel="noopener noreferrer"
                className="underline hover:text-foreground"
              >
                aboutads.info
              </a>
              .
            </p>
          </Block>

          <Block title="5. Compartilhamento com terceiros">
            <p>
              Não vendemos nem alugamos seus dados. Compartilhamos informações apenas com categorias
              específicas de destinatários, quando estritamente necessário:
            </p>
            <ul className="list-disc pl-5 space-y-1">
              <li>
                <strong className="text-foreground">Provedores de infraestrutura</strong>{" "}
                (hospedagem, banco de dados, e-mail) — para operação técnica do serviço.
              </li>
              <li>
                <strong className="text-foreground">Paddle.com</strong> — atua como Merchant of
                Record para venda de assinaturas, processamento de pagamentos, gestão de
                assinaturas, faturamento e conformidade fiscal. A Paddle trata seus dados de
                pagamento conforme a própria política de privacidade dela.
              </li>
              <li>
                <strong className="text-foreground">Provedores de IA</strong> (ex: Google, OpenAI
                via gateway) — apenas as mensagens enviadas ao assistente de IA Pro são processadas
                por esses serviços para gerar respostas.
              </li>
              <li>
                <strong className="text-foreground">Autoridades públicas</strong> — quando exigido
                por lei ou ordem judicial.
              </li>
            </ul>
          </Block>

          <Block title="6. Seus direitos (LGPD)">
            <p>
              Em conformidade com a Lei Geral de Proteção de Dados (Lei 13.709/2018), você tem
              direito a:
            </p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Confirmar a existência de tratamento de dados</li>
              <li>Acessar seus dados</li>
              <li>Corrigir dados incompletos ou desatualizados</li>
              <li>Solicitar a exclusão dos seus dados</li>
              <li>Revogar consentimento</li>
            </ul>
            <p>
              Como os dados ficam apenas no seu dispositivo, você pode exercer o direito de exclusão
              a qualquer momento limpando o armazenamento local do navegador (configurações &gt;
              privacidade &gt; limpar dados de navegação).
            </p>
          </Block>

          <Block title="7. Retenção de dados">
            <p>
              Mantemos seus dados de conta e assinatura enquanto sua conta estiver ativa e pelo
              prazo necessário para cumprir obrigações legais (fiscais, contábeis). Após o
              encerramento, dados são excluídos ou anonimizados, exceto quando a lei exigir
              retenção. Dados de pagamento são retidos pela Paddle conforme a política dela.
            </p>
          </Block>

          <Block title="8. Segurança">
            <p>
              Adotamos medidas técnicas e organizacionais apropriadas para proteger seus dados:
              HTTPS de ponta a ponta, criptografia em trânsito, controles de acesso, autenticação
              segura e princípio do menor privilégio. Nenhum sistema é 100% imune, mas trabalhamos
              para minimizar riscos.
            </p>
          </Block>

          <Block title="9. Crianças e adolescentes">
            <p>
              O MEI Toolbox é direcionado a maiores de 18 anos (idade mínima para abrir um MEI). Não
              coletamos intencionalmente dados de crianças e adolescentes.
            </p>
          </Block>

          <Block title="10. Alterações nesta política">
            <p>
              Podemos atualizar esta política para refletir mudanças no produto ou na legislação. A
              data da última atualização sempre estará no topo da página. Mudanças significativas
              serão destacadas no site.
            </p>
          </Block>

          <Block title="11. Contato">
            <p className="flex items-start gap-2">
              <Mail className="h-4 w-4 mt-1 text-primary shrink-0" />
              <span>
                Dúvidas sobre privacidade ou tratamento de dados? Fale com a gente em{" "}
                <strong className="text-foreground">contato@meitoolbox.com.br</strong>.
              </span>
            </p>
          </Block>
        </article>

        <div className="mt-12 rounded-2xl border border-border bg-surface/60 p-6 text-center">
          <p className="text-sm text-muted-foreground">Quer voltar a usar as ferramentas?</p>
          <Link
            to="/ferramentas"
            className="mt-4 inline-flex items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-sm font-bold text-primary-foreground shadow-soft hover:opacity-90 transition"
          >
            Ver todas as ferramentas <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>
    </>
  );
}

function Highlight({ icon, title, text }: { icon: React.ReactNode; title: string; text: string }) {
  return (
    <div className="rounded-2xl border border-border bg-card p-5 shadow-soft">
      <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary">
        {icon}
      </div>
      <div className="mt-3 font-semibold text-foreground">{title}</div>
      <p className="mt-1 text-sm text-muted-foreground leading-snug">{text}</p>
    </div>
  );
}

function Block({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section>
      <h2 className="font-display text-2xl font-bold text-foreground mb-3">{title}</h2>
      <div className="space-y-3">{children}</div>
    </section>
  );
}
